/** API client for the admin backend.
 *
 * Auth strategy: the backend sets an HttpOnly `access_token` cookie on
 * /api/auth/login and a non-HttpOnly `csrf_token` cookie alongside it.
 * Every mutating request must echo the CSRF token in the `X-CSRF-Token`
 * header (double-submit pattern). `credentials: 'include'` makes fetch
 * attach both cookies automatically.
 */

const BASE = '/api/admin';
const CSRF_COOKIE = 'csrf_token';

// Robust cookie reader: split-and-trim instead of regex. Avoids name-prefix
// collisions (a stale `csrf_token_old` cookie was matching the regex via
// the unescaped name). Also no need to regex-escape arbitrary names.
function readCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const parts = document.cookie.split(';');
  for (const raw of parts) {
    const eq = raw.indexOf('=');
    if (eq < 0) continue;
    const k = raw.slice(0, eq).trim();
    if (k === name) {
      try { return decodeURIComponent(raw.slice(eq + 1).trim()); }
      catch { return raw.slice(eq + 1).trim(); }
    }
  }
  return null;
}

async function request(path, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const csrf = readCookie(CSRF_COOKIE);
  const isMutation = !['GET', 'HEAD', 'OPTIONS'].includes(method);
  const isFormData = (
    typeof FormData !== 'undefined'
    && options.body instanceof FormData
  );
  const headers = {
    ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
    ...(isMutation && csrf ? { 'X-CSRF-Token': csrf } : {}),
    ...options.headers,
  };
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
    ...(method === 'GET' ? { cache: 'no-store' } : {}),
  });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('auth:logout'));
    throw new Error('401: not authenticated');
  }
  if (res.status === 403) {
    // Most 403s here are CSRF-related: stale session from before the cookie
    // auth migration. The csrf_token cookie isn't set, so request() can't
    // attach X-CSRF-Token. Force re-login to get fresh cookies.
    const body = await res.text();
    const lower = body.toLowerCase();
    if (
      lower.includes('csrf')
      || lower.includes('invalid or missing api key')
    ) {
      window.dispatchEvent(new CustomEvent('auth:logout'));
      throw new Error('Сессия истекла — войдите снова');
    }
    throw new Error(`403: ${body}`);
  }
  if (!res.ok) {
    // FastAPI returns errors as {"detail": "..."} — strip the JSON wrapper
    // so toast messages read "Send error: Пользователь ещё не начал диалог
    // с ботом" instead of the raw `{"detail":"…"}` blob. Falls back to the
    // verbatim body for non-JSON responses (e.g. nginx-rendered 502).
    const body = await res.text();
    let pretty = body;
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed.detail === 'string') pretty = parsed.detail;
    } catch { /* not JSON — keep raw text */ }
    throw new Error(`${res.status}: ${pretty}`);
  }
  return res.json();
}

// ── Auth ──
export async function login(email, password) {
  // OAuth2 form expects username + password (we map email to username)
  const body = new URLSearchParams({ username: email, password });
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
    credentials: 'include',
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || 'Login failed');
  }
  const data = await res.json();
  return data.user;
}
export async function getMe() {
  const res = await fetch('/api/auth/me', { credentials: 'include' });
  if (!res.ok) return null;
  return res.json();
}
export async function logout() {
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-Token': readCookie(CSRF_COOKIE) || '' },
    });
  } catch { /* ignore — we're clearing anyway */ }
}

// ── Users (admin only) ──
export const getUsers = () => request('/users');
export const createUser = (data) =>
  request('/users', { method: 'POST', body: JSON.stringify(data) });
export const updateUser = (id, data) =>
  request(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) });

// Trigger TG history backfill for a personal-session dialog.
// `beforeMessageId` is the oldest known external_message_id integer (extract
// from "tg_personal_NNN"); pass null/0 to fetch from the latest.
export const loadDialogHistory = (dialogId, { limit = 50, beforeMessageId = null } = {}) =>
  request(`/dialogs/${dialogId}/load-history`, {
    method: 'POST',
    body: JSON.stringify({
      limit,
      before_message_id: beforeMessageId,
    }),
  });

// Operator typing → broadcasts an SSE event so other operators in the
// same dialog see "X is typing…". Call with is_typing=false on stop/blur.
export const sendTyping = (dialogId, isTyping) =>
  request(`/dialogs/${dialogId}/typing`, {
    method: 'POST',
    body: JSON.stringify({ is_typing: !!isTyping }),
  });

// ── SSE real-time updates ──
// EventSource auto-reconnects (with 3s backoff by default) and auto-attaches
// cookies on same-origin URLs — auth "just works" with our HttpOnly cookie.
// Caller must hold the returned object and .close() on unmount.
export function subscribeDialogEvents(dialogId, onEvent, onError) {
  const es = new EventSource(`/api/admin/dialogs/${dialogId}/stream`, {
    withCredentials: true,
  });
  es.onmessage = (e) => {
    try { onEvent(JSON.parse(e.data)); } catch { /* ignore non-JSON */ }
  };
  if (onError) es.onerror = onError;
  return es;
}

export function subscribeDialogsList(onEvent, onError) {
  const es = new EventSource('/api/admin/dialogs/stream', {
    withCredentials: true,
  });
  es.onmessage = (e) => {
    try { onEvent(JSON.parse(e.data)); } catch { /* ignore */ }
  };
  if (onError) es.onerror = onError;
  return es;
}

// ── Subscription ──
export const getSubscription = () => request('/subscription');

export const getBilling = () => request('/billing');
export const getBillingOrders = () => request('/billing/orders');

export const createBillingCheckout = (data) =>
  request('/billing/checkout', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const resumeBillingOrder = (id) =>
  request(`/billing/orders/${encodeURIComponent(id)}/resume`, {
    method: 'POST',
  });

export const verifyBillingOrder = (id) =>
  request(`/billing/orders/${encodeURIComponent(id)}/verify`, {
    method: 'POST',
  });


// ── Channels ──
export const getChannels = () => request('/channels');
export const createChannel = (data) =>
  request('/channels', { method: 'POST', body: JSON.stringify(data) });
export const updateChannel = (id, data) =>
  request(`/channels/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
// Soft-delete (default): channel.is_active = false. Keeps history.
export const deleteChannel = (id) =>
  request(`/channels/${id}`, { method: 'DELETE' });
// Hard delete: physically remove from DB. Backend refuses (409) if there
// are dialogs still attached — caller has to clear them first OR pass
// `cascade=true` to wipe history + channel atomically.
export const deleteChannelHard = (id) =>
  request(`/channels/${id}?hard=true`, { method: 'DELETE' });
export const deleteChannelHardCascade = (id) =>
  request(`/channels/${id}?hard=true&cascade=true`, { method: 'DELETE' });
// Toggle: enable an inactive channel (and vice-versa via updateChannel).
export const enableChannel = (id) =>
  updateChannel(id, { is_active: true });
export const testChannel = (id) =>
  request(`/channels/${id}/test`, { method: 'POST' });
// TG-bot diagnostics: ask Telegram what URL it has on file + whether
// last delivery attempts failed. Pairs with reregisterChannelWebhook
// to recover when setWebhook never reached TG (the #1 reason a bot
// "doesn't receive anything").
export const channelWebhookInfo = (id) =>
  request(`/channels/${id}/webhook-info`);
export const reregisterChannelWebhook = (id) =>
  request(`/channels/${id}/register-webhook`, { method: 'POST' });

// ── Bitrix ──
export const registerBitrix = () =>
  request('/bitrix/register', { method: 'POST' });
export const activateBitrixLine = (lineId) =>
  request(`/bitrix/activate/${lineId}`, { method: 'POST' });
export const bitrixHealth = () => request('/bitrix/health');

export const getBitrixState = () =>
  request('/bitrix/state');

export const getBitrixUsers = () =>
  request('/bitrix/users');

export const saveBitrixConfig = (config) =>
  request('/bitrix/config', {
    method: 'PUT',
    body: JSON.stringify(config),
  });

export const startBitrixOAuth = (portalDomain) =>
  request('/bitrix/oauth/start', {
    method: 'POST',
    body: JSON.stringify({ portal_domain: portalDomain }),
  });

export const getBitrixOpenLines = () =>
  request('/bitrix/open-lines');

export const createBitrixOpenLine = (name) =>
  request('/bitrix/open-lines', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });

export const getBitrixBindings = () =>
  request('/bitrix/bindings');

export const saveBitrixBindings = (bindings) =>
  request('/bitrix/bindings', {
    method: 'PUT',
    body: JSON.stringify({ bindings }),
  });

// ── Sessions ──
export const getSessions = () => request('/sessions');

// ── History backfill (admin "История" tab) ──
// Start a full-account Telegram history import for a personal session.
// push_to_bitrix=false → messages land in our DB only, never in Bitrix.
export const startHistoryBackfill = (sessionId, { sinceDate = null, pushToBitrix = false } = {}) =>
  request(`/sessions/${sessionId}/history-backfill`, {
    method: 'POST',
    body: JSON.stringify({ since_date: sinceDate, push_to_bitrix: pushToBitrix }),
  });
// List all backfill jobs for the admin table (in-progress + finished).
export const listHistoryBackfill = () => request('/history-backfill');
export const createSession = (data) =>
  request('/sessions', { method: 'POST', body: JSON.stringify(data) });
export const requestQR = (id) =>
  request(`/sessions/${id}/qr`, { method: 'POST' });
export const getQR = (id) =>
  request(`/sessions/${id}/qr`);
export const disconnectSession = (id) =>
  request(`/sessions/${id}/disconnect`, { method: 'POST' });
// Remove account credentials/runtime while preserving the durable channel
// and all existing dialogs/messages.
export const deleteSession = (id) =>
  request(`/sessions/${id}`, { method: 'DELETE' });
// `force=true` requests a fresh worker generation even when the current
// generation is still running. Backend rotates the durable lease and creates
// a replacement container; it never reuses a stale worker generation.
export const startSession = (id, { force = false } = {}) =>
  request(
    `/sessions/${id}/start${force ? '?force_restart=true' : ''}`,
    { method: 'POST' },
  );
export const providePhone = (id, value) =>
  request(`/sessions/${id}/provide-phone`, { method: 'POST', body: JSON.stringify({ value }) });
export const provideCode = (id, value) =>
  request(`/sessions/${id}/provide-code`, { method: 'POST', body: JSON.stringify({ value }) });
export const resendCode = (id) =>
  request(`/sessions/${id}/resend-code`, { method: 'POST' });
export const providePassword = (id, value) =>
  request(`/sessions/${id}/provide-password`, { method: 'POST', body: JSON.stringify({ value }) });

// ── Proxies ──
export const getProxies = () => request('/proxies');
export const addProxy = (data) =>
  request('/proxies', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const setSessionProxy = (sessionId, proxyId) =>
  request(`/sessions/${sessionId}/proxy`, {
    method: 'PATCH',
    body: JSON.stringify({
      proxy_id: proxyId || null,
    }),
  });

// ── Stats ──
export const getStats = () => request('/stats');

// ── Dialogs / Messages ──
export const getDialogs = () => request('/dialogs');
export const deleteDialog = (dialogId) =>
  request(`/dialogs/${dialogId}`, { method: 'DELETE' });
export const getMessages = (dialogId, opts = {}) => {
  // opts: { limit?, before?, after? }
  const params = new URLSearchParams();
  params.set('limit', opts.limit ?? 50);
  if (opts.before) params.set('before', opts.before);
  if (opts.after) params.set('after', opts.after);
  return request(`/dialogs/${dialogId}/messages?${params}`);
};
export const sendMessage = (data) =>
  request('/messages/send', { method: 'POST', body: JSON.stringify(data) });

export const sendMediaGroup = (data) =>
  request('/messages/send-media-group', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const uploadMedia = ({
  file,
  file_name,
  media_type,
}) => {
  const body = new FormData();

  body.append(
    'file',
    file,
    file_name || file?.name || 'file',
  );

  if (media_type) {
    body.append('media_type', media_type);
  }

  return request('/messages/upload-media', {
    method: 'POST',
    body,
  });
};
export const markDialogRead = (dialogId) =>
  request(`/dialogs/${dialogId}/read`, { method: 'POST' });
export const editMessage = (dialogId, messageId, text) =>
  request(`/dialogs/${dialogId}/messages/${messageId}`, {
    method: 'PATCH', body: JSON.stringify({ text }),
  });
export const deleteMessage = (dialogId, messageId) =>
  request(`/dialogs/${dialogId}/messages/${messageId}`, { method: 'DELETE' });

// ── Health ──
export const getHealth = () =>
  fetch('/health/full').then((r) => r.json());


// Personal account <-> Bitrix employee M2M routing.
export const getBitrixOperatorRouting = () =>
  request('/integration-bindings/operator-routing');

export const saveBitrixOperatorRouting = (items) =>
  request(
    '/integration-bindings/operator-routing',
    {
      method: 'PUT',
      body: JSON.stringify({ items }),
    },
  );


// Банковские счета текущей организации.
export const getBankInvoiceConfig = () =>
  request('/billing/bank-invoices/config');

export const listBankInvoices = () =>
  request('/billing/bank-invoices');

export const createBankInvoice = (data) =>
  request('/billing/bank-invoices', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export async function downloadBankInvoicePdf(invoiceId) {
  const id = encodeURIComponent(String(invoiceId));
  const response = await fetch(
    `${BASE}/billing/bank-invoices/${id}/pdf`,
    { credentials: 'include', cache: 'no-store' },
  );

  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent('auth:logout'));
    throw new Error('Войдите в личный кабинет повторно');
  }

  if (!response.ok) {
    throw new Error('Не удалось скачать счёт');
  }

  const blob = await response.blob();

  if (
    !blob.type.startsWith('application/pdf')
    || blob.size < 100
  ) {
    throw new Error('Получен некорректный документ');
  }

  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download = `svyazka-invoice-${id}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
}
